import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import PhotoDropzone from "@/components/ui/PhotoDropzone";
import { packMl, hasMlContent } from "@/lib/ml";
import { GLOBAL_COUNTRIES } from "@/lib/data";
import { toast } from "sonner";

const ease = [0.22, 1, 0.36, 1];

const inputCls = "w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 focus:outline-none focus:border-[color:var(--rust)]";

export default function ReportMissing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", preferred_name: "", age: "", gender: "",
    photo_path: "", languages: "",
    last_seen_date: "", last_seen_time: "",
    clothing: "", vehicle: "", companions: "",
    country: "", region: "", district: "", city: "", area: "", precision: "city",
    is_minor: false,
  });
  const [description, setDescription] = useState("");
  const [identifying, setIdentifying] = useState("");
  const [circumstances, setCircumstances] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  async function submit(e) {
    e.preventDefault();
    if (!f.name.trim() || !f.last_seen_date.trim() || !f.country.trim()) {
      toast.error("Name, last-seen date and country are needed.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/v1/missing", {
        name: f.name.trim(),
        preferred_name: f.preferred_name.trim() || undefined,
        age: f.age === "" ? undefined : parseInt(f.age, 10),
        gender: f.gender || undefined,
        photo_path: f.photo_path || undefined,
        languages: f.languages.trim() || undefined,
        last_seen_date: f.last_seen_date.trim(),
        last_seen_time: f.last_seen_time.trim() || undefined,
        clothing: f.clothing.trim() || undefined,
        vehicle: f.vehicle.trim() || undefined,
        companions: f.companions.trim() || undefined,
        description: hasMlContent(description) ? packMl(description) : undefined,
        identifying: hasMlContent(identifying) ? packMl(identifying) : undefined,
        circumstances: hasMlContent(circumstances) ? packMl(circumstances) : undefined,
        is_minor: !!f.is_minor,
        loc: {
          country: f.country.trim(),
          country_code: GLOBAL_COUNTRIES.find((c) => c.name === f.country)?.code?.toUpperCase(),
          region: f.region.trim() || undefined,
          district: f.district.trim() || undefined,
          city: f.city.trim() || undefined,
          area: f.area.trim() || undefined,
          precision: f.precision,
        },
      });
      setCreated(data.case);
      toast.success(`Case ${data.case.case_id} opened.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (!user) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="report-auth-gate">
        <h1 className="heritage-title text-4xl md:text-5xl">Sign in to report a missing person</h1>
        <p className="mt-4 text-[color:var(--ink-soft)] max-w-lg mx-auto">
          Every case is kept by a named family member — that is how we protect families from scams and false reports.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/app/login" className="btn-wood" data-testid="report-login">Sign in</Link>
          <Link to="/app/register" className="btn-ghost" data-testid="report-register">Create account</Link>
        </div>
      </main>
    );
  }

  if (created) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="report-success">
        <CheckCircle2 size={36} className="mx-auto text-[#3d5a3c]" />
        <h1 className="heritage-title text-4xl md:text-5xl mt-4">The case is open.</h1>
        <p className="mt-4 numeric-index text-[color:var(--ink-soft)]" data-testid="report-case-id">{created.case_id}</p>
        <p className="mt-4 text-[color:var(--ink-soft)] max-w-lg mx-auto">
          The page is live and shareable now. A moderator will review it shortly — until then it carries an
          "awaiting review" note. Share it widely; every pair of eyes matters.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <button onClick={() => nav(`/missing/case/${created.case_id}`)} className="btn-rust" data-testid="report-view-case">
            Open the case page
          </button>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/missing/case/${created.case_id}`);
              toast.success("Link copied.");
            }}
            className="btn-ghost"
            data-testid="report-copy-link"
          >
            Copy share link
          </button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="report-missing-page" className="pt-40 md:pt-48">
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <Link to="/missing" className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-8" data-testid="report-back">
          <ArrowLeft size={12} /> Missing &amp; Found
        </Link>
        <div className="overline text-[color:var(--ink-soft)] mb-4">§ Open a case</div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="heritage-title text-4xl md:text-6xl leading-tight"
        >
          Report a missing person
        </motion.h1>
        <p className="mt-4 text-[16px] leading-[1.65] text-[color:var(--ink-soft)]">
          Fill what you know — you can always add more later. Share only what is safe:
          approximate places, no home addresses, no private phone numbers.
          People with information will reach you through the case page, privately.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="report-form">
          {/* Identity */}
          <div className="border border-[color:var(--sepia)]/25 p-6 md:p-8">
            <div className="overline mb-5">Who is missing</div>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="overline block mb-1">Full name *</span>
                <input data-testid="report-name" value={f.name} onChange={set("name")} className={inputCls} required />
              </label>
              <label className="block">
                <span className="overline block mb-1">Known as</span>
                <input data-testid="report-preferred" value={f.preferred_name} onChange={set("preferred_name")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Age</span>
                <input data-testid="report-age" type="number" min="0" max="130" value={f.age} onChange={set("age")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Gender</span>
                <select data-testid="report-gender" value={f.gender} onChange={set("gender")} className={inputCls}>
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="overline block mb-1">Languages spoken</span>
                <input data-testid="report-languages" value={f.languages} onChange={set("languages")} placeholder="Hindi, English…" className={inputCls} />
              </label>
            </div>
            <div className="mt-5">
              <div className="overline mb-2">Photograph</div>
              <PhotoDropzone testid="report-photo" value={f.photo_path} onChange={(v) => setF({ ...f, photo_path: v })} />
            </div>
            <label className="mt-5 flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="report-minor"
                checked={f.is_minor}
                onChange={(e) => setF({ ...f, is_minor: e.target.checked })}
                className="accent-[color:var(--rust)]"
              />
              This is a child under 18 <span className="text-[color:var(--ink-soft)]">(extra privacy is applied automatically)</span>
            </label>
          </div>

          {/* Disappearance */}
          <div className="border border-[color:var(--sepia)]/25 p-6 md:p-8">
            <div className="overline mb-5">The disappearance</div>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="overline block mb-1">Date last seen *</span>
                <input data-testid="report-date" value={f.last_seen_date} onChange={set("last_seen_date")} placeholder="2026-08-20" className={inputCls} required />
              </label>
              <label className="block">
                <span className="overline block mb-1">Time, if known</span>
                <input data-testid="report-time" value={f.last_seen_time} onChange={set("last_seen_time")} placeholder="around 6 pm" className={inputCls} />
              </label>
            </div>
            <div className="mt-5">
              <MultilingualEditor
                as="textarea"
                rows={3}
                testid="report-circumstances"
                label="Circumstances"
                value={circumstances}
                onChange={setCircumstances}
                maxLength={2000}
                placeholder="What happened, as plainly as you can tell it…"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-5 mt-5">
              <label className="block">
                <span className="overline block mb-1">Clothing</span>
                <input data-testid="report-clothing" value={f.clothing} onChange={set("clothing")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Vehicle</span>
                <input data-testid="report-vehicle" value={f.vehicle} onChange={set("vehicle")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">Travelling with</span>
                <input data-testid="report-companions" value={f.companions} onChange={set("companions")} className={inputCls} />
              </label>
            </div>
          </div>

          {/* Where */}
          <div className="border border-[color:var(--sepia)]/25 p-6 md:p-8">
            <div className="overline mb-2">Where they were last seen</div>
            <p className="text-xs text-[color:var(--ink-soft)] mb-5 flex items-start gap-1.5">
              <ShieldCheck size={13} className="shrink-0 mt-px" />
              Share an approximate place — a neighbourhood or town, never a street address.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="overline block mb-1">Country *</span>
                <input data-testid="report-country" list="report-countries" value={f.country} onChange={set("country")} className={inputCls} required />
                <datalist id="report-countries">
                  {GLOBAL_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.name} />
                  ))}
                </datalist>
              </label>
              <label className="block">
                <span className="overline block mb-1">State / Province</span>
                <input data-testid="report-region" value={f.region} onChange={set("region")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">District / County</span>
                <input data-testid="report-district" value={f.district} onChange={set("district")} className={inputCls} />
              </label>
              <label className="block">
                <span className="overline block mb-1">City / Town / Village</span>
                <input data-testid="report-city" value={f.city} onChange={set("city")} className={inputCls} />
              </label>
            </div>
          </div>

          {/* About them */}
          <div className="border border-[color:var(--sepia)]/25 p-6 md:p-8">
            <div className="overline mb-5">About them</div>
            <MultilingualEditor
              as="textarea"
              rows={4}
              testid="report-description"
              label="Description"
              value={description}
              onChange={setDescription}
              maxLength={3000}
              placeholder="Height, build, how they look and speak, what they love…"
            />
            <div className="mt-5">
              <MultilingualEditor
                as="input"
                testid="report-identifying"
                label="Identifying features"
                value={identifying}
                onChange={setIdentifying}
                maxLength={800}
                placeholder="Scars, marks, tattoos, glasses, a way of walking…"
              />
            </div>
          </div>

          <button type="submit" data-testid="report-submit" disabled={saving} className="btn-rust !py-3.5 !px-8 disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Open the case
          </button>
          <p className="text-xs text-[color:var(--ink-soft)] leading-relaxed">
            The case becomes public immediately so people can help, and is flagged for moderator review.
            You remain the case administrator — you can edit, update the status, or withdraw at any time.
            FamilyTree.Team is an information platform, not a police authority — please also file an official police report.
          </p>
        </form>
      </section>
    </main>
  );
}
